<script setup lang="ts">
import { TgWappenSvg, ShWappenSvg } from '@/helpers/images';
import { ref, watch, type Ref } from 'vue';
import { instance as api } from '@/helpers/api';

const value = defineModel<boolean>('value');

withDefaults(
    defineProps<{
        loading?: boolean;
    }>(),
    {
        value: false,
        loading: false
    }
);

defineEmits(['close']);

const tgHelp: Ref<string> = ref('');
const shHelp: Ref<string> = ref('');
const afeHelp: Ref<string> = ref('');

watch(value, async (isOpen) => {
    if (!isOpen) {
        return;
    }
    const res = await api().get('/hilfetext/load');
    tgHelp.value = res.data.tg;
    shHelp.value = res.data.sh;
    afeHelp.value = res.data.afe;
})

</script>

<template>
    <v-dialog :model-value="value" persistent :max-width="800" scrollable @click:outside="$emit('close')">
        <v-card rounded="lg">
            <slot name="title">
                <v-card-title class="d-flex align-center py-4 px-6">
                    <v-icon color="action" class="mr-3" size="22">fas fa-circle-question</v-icon>
                    {{ $t('hilfe_kontakt') }}
                </v-card-title>
            </slot>
            <v-divider />
            <slot>
                <v-card-text class="pa-6">
                    <p class="text-body-2 text-medium-emphasis mb-4">
                        Haben Sie technische Fragen zum Projekt oder zu einem bestimmten Gesuch? Dann wenden Sie sich
                        an:
                    </p>

                    <section class="help-section">
                        <div class="help-section__header">
                            <img :src="TgWappenSvg" height="26" alt="Thurgau Wappen" />
                            <h3>Kanton Thurgau</h3>
                        </div>
                        <div class="help-content" v-html="tgHelp"></div>
                    </section>

                    <section class="help-section mt-4">
                        <div class="help-section__header">
                            <img :src="ShWappenSvg" height="26" alt="Schaffhausen Wappen" />
                            <h3>Kanton Schaffhausen</h3>
                        </div>
                        <div class="help-content" v-html="shHelp"></div>
                    </section>

                    <v-divider class="my-6" />

                    <p class="text-body-2 text-medium-emphasis mb-4">
                        Haben Sie technische Fragen zum Energieförderportal? Dann wenden Sie sich an:
                    </p>

                    <section class="help-section">
                        <div class="help-section__header">
                            <v-icon color="action" size="20">fas fa-bolt</v-icon>
                            <h3>Energieförderportal</h3>
                        </div>
                        <div class="help-content" v-html="afeHelp"></div>
                    </section>
                </v-card-text>
            </slot>
            <v-divider />
            <slot name="actions">
                <v-card-actions class="pa-4">
                    <v-spacer />
                    <v-btn color="decline" variant="text" :loading="loading" @click="$emit('close')">
                        {{ $t('close') }}
                    </v-btn>
                </v-card-actions>
            </slot>
        </v-card>
    </v-dialog>
</template>

<style scoped>
.v-card__text,
.v-card__title {
    word-break: normal !important;
}

address {
    font-style: normal;
}

.help-section {
    background: rgba(var(--v-theme-on-surface), 0.045);
    border: thin solid rgba(var(--v-theme-on-surface), 0.08);
    border-radius: 10px;
    padding: 1rem 1.25rem;
}

.help-section__header {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    margin-bottom: 0.75rem;
}

.help-section__header h3 {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
}

.help-content :deep(p) {
    margin: 0 0 0.75em;
    line-height: 1.5;
}

.help-content :deep(p:last-child) {
    margin-bottom: 0;
}

.help-content :deep(ul) {
    margin: 0 0 0.75em;
    padding-left: 0;
    list-style: none;
}

.help-content :deep(ol) {
    margin: 0 0 0.75em;
    padding-left: 1.4em;
}

.help-content :deep(ul > li) {
    border-bottom: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
    padding-bottom: 0.4em;
    margin-bottom: 0.4em;
}

.help-content :deep(ul > li:last-child) {
    border-bottom: none;
    padding-bottom: 0;
    margin-bottom: 0;
}

.help-content :deep(li) {
    line-height: 1.5;
}

.help-content :deep(li > p) {
    margin-bottom: 0.2em;
}

.help-content :deep(a) {
    color: rgb(var(--v-theme-anchor));
    text-decoration: underline;
    overflow-wrap: anywhere;
}

.help-content :deep(a:hover) {
    text-decoration: none;
}

.help-content :deep(strong) {
    font-weight: 600;
}
</style>
